import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import GenericTable from "../../components/GenericTable";
import { useClientTags } from "../../hooks/useClientTags";

const ClientTags = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const { data, refetch, error, isLoading, isFetching, createMutation, updateMutation, deleteMutation } =
    useClientTags({ search, limit: 500, includeUsed: false });

  const [errorMessage, setErrorMessage] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, nome}
  const [confirmDelete, setConfirmDelete] = useState(null); // id
  const [isProcessing, setIsProcessing] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (error) setErrorMessage("Erro ao carregar tags de cliente.");
    else setErrorMessage(null);
  }, [error]);

  // debounce da busca (padrão Settings)
  useEffect(() => {
    const t = setTimeout(() => {
      const term = String(searchInput || "").trim();
      setSearch(term);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const rows = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return list
      .filter((t) => t && t.source !== "used") // segurança caso backend retorne algo inesperado
      .map((t) => ({
        id: t.id,
        nome: t.nome,
        updatedAt: t.updatedAt || null,
      }));
  }, [data]);

  const busy =
    Boolean(isLoading) ||
    Boolean(isFetching) ||
    Boolean(isProcessing) ||
    Boolean(createMutation?.isLoading) ||
    Boolean(updateMutation?.isLoading) ||
    Boolean(deleteMutation?.isLoading);

  const rowCount = rows.length;
  const paginatedRows = useMemo(() => {
    const start = page * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const columns = useMemo(
    () => [
      {
        field: "nome",
        headerName: "Nome",
        flex: 1,
        minWidth: 320,
        renderCell: (p) => (
          <Stack spacing={0.3} sx={{ height: "100%", justifyContent: "center" }}>
            <Typography fontWeight={600} noWrap>
              {p.row.nome || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {p.row.id != null ? `ID ${p.row.id}` : "—"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "actions",
        headerName: "Ações",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%" }}>
              <Tooltip title="Editar">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setEditing({ id: row.id, nome: row.nome })}
                    disabled={busy}
                  >
                    <EditRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Excluir">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setConfirmDelete(row.id)}
                    disabled={busy}
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [busy]
  );

  const openCreate = () => setEditing({ id: null, nome: "" });
  const closeForm = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const nome = String(editing.nome || "").trim();
    if (!nome) {
      setErrorMessage("Informe o nome da tag.");
      return;
    }

    try {
      setErrorMessage(null);
      setIsProcessing(true);
      if (editing.id) {
        await updateMutation.mutateAsync({ id: editing.id, nome });
      } else {
        await createMutation.mutateAsync({ nome });
      }
      await refetch();
      closeForm();
      setSnack({
        open: true,
        message: editing.id ? "Tag atualizada com sucesso." : "Tag criada com sucesso.",
        severity: "success",
      });
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao salvar a tag.");
      setSnack({ open: true, message: "Falha ao salvar.", severity: "error" });
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmDeleteRow = async () => {
    if (!confirmDelete) return;
    try {
      setErrorMessage(null);
      setIsProcessing(true);
      await deleteMutation.mutateAsync(confirmDelete);
      await refetch();
      setConfirmDelete(null);
      setSnack({ open: true, message: "Tag excluída com sucesso.", severity: "success" });
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao excluir a tag.");
      setSnack({ open: true, message: "Falha ao excluir.", severity: "error" });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ pt: 1, overflowX: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          Tags de cliente
        </Typography>
        <Stack direction={isMobile ? "column" : "row"} spacing={1} alignItems="center" width={isMobile ? "100%" : "auto"}>
          <Button
            variant="contained"
            color="primary"
            fullWidth={isMobile}
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            Nova tag
          </Button>
        </Stack>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <GenericTable
        rows={paginatedRows}
        columns={columns}
        rowCount={rowCount}
        pageSize={pageSize}
        page={page}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        onSearch={(term) => setSearchInput(term)}
        isLoading={busy}
        exportFileName="client-tags"
      />

      {/* Form (Create/Edit) */}
      <Dialog open={Boolean(editing)} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? "Editar tag" : "Nova tag"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Nome"
            value={editing?.nome ?? ""}
            onChange={(e) => setEditing((p) => ({ ...(p || {}), nome: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>Cancelar</Button>
          <Button onClick={save} variant="contained" disabled={busy}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir tag</DialogTitle>
        <DialogContent dividers>Tem certeza que deseja excluir esta tag?</DialogContent>
        <DialogActions>
          {!busy && <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>}
          {!busy && (
            <Button color="error" variant="contained" onClick={confirmDeleteRow}>
            Excluir
          </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
};

export default ClientTags;

